import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, Button,
  Avatar, Stack, Chip, Divider, Alert, CircularProgress, InputAdornment,
  Tooltip,
} from '@mui/material';
import PersonOutlineIcon   from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon   from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon   from '@mui/icons-material/PhoneOutlined';
import WorkOutlineIcon     from '@mui/icons-material/WorkOutline';
import LockResetIcon       from '@mui/icons-material/LockReset';
import SaveIcon            from '@mui/icons-material/Save';
import BadgeOutlinedIcon   from '@mui/icons-material/BadgeOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import ChangePassword from './ChangePassword';
import globals from '../../services/globals';
import apiService from '../../services/apiServices';

const ROLE_LABELS = {
  admin:    { label: 'Admin',    color: '#1976d2' },
  manager:  { label: 'Menaxher', color: '#7b1fa2' },
  menaxher: { label: 'Menaxher', color: '#7b1fa2' },
  staff:    { label: 'Staff',    color: '#2e7d32' },
  employee: { label: 'Employee', color: '#2e7d32' },
  hr:       { label: 'HR',       color: '#c62828' },
};

function initials(f = '', l = '') {
  return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?';
}

export default function UserProfile() {
  const currentUser = globals().getLogedInUser();
  const userId      = currentUser?.data?.id;
  const role        = (currentUser?.data?.roles?.[0] || '').toLowerCase();
  const roleInfo    = ROLE_LABELS[role] || { label: role || 'Staff', color: '#546e7a' };

  const fileInputRef = useRef(null);

  const [form, setForm]         = useState({
    firstname: currentUser?.data?.firstname || '',
    lastname:  currentUser?.data?.lastname  || '',
    phone:     '',
    job_title: '',
    email:     currentUser?.data?.email || '',
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBase64,  setPhotoBase64]  = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [success, setSuccess]   = useState('');
  const [error,   setError]     = useState('');
  const [cpOpen,  setCpOpen]    = useState(false);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    apiService().getById('users', userId)
      .then(r => {
        if (r?.success && r?.data) {
          const d = r.data;
          setForm({
            firstname: d.firstname || '',
            lastname:  d.lastname  || '',
            phone:     d.phone     || '',
            job_title: d.job_title || '',
            email:     d.email     || '',
          });
          if (d.profile_photo) setPhotoPreview(d.profile_photo);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const handleChange = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Fotoja nuk mund të jetë më e madhe se 5 MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoPreview(ev.target.result);
      setPhotoBase64(ev.target.result);
    };
    reader.readAsDataURL(file);
    // reset so same file can be re-selected
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      const r = await apiService().put('users', {
        Id:          userId,
        Name:        form.firstname,
        Surname:     form.lastname,
        Phone:       form.phone,
        JobTitle:    form.job_title,
        ...(photoBase64 !== null ? { PhotoBase64: photoBase64 } : {}),
      });
      if (r?.success) {
        setSuccess('Të dhënat u ruajtën me sukses.');
        // Përditëso emrin në sesion
        try {
          const stored = JSON.parse(localStorage.getItem('logedInUser') || sessionStorage.getItem('logedInUser') || '{}');
          if (stored?.data) {
            stored.data.firstname = form.firstname;
            stored.data.lastname  = form.lastname;
            const str = JSON.stringify(stored);
            if (localStorage.getItem('logedInUser'))   localStorage.setItem('logedInUser',   str);
            if (sessionStorage.getItem('logedInUser')) sessionStorage.setItem('logedInUser', str);
          }
        } catch {}
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(r?.error || 'Ruajtja dështoi.');
      }
    } catch (e) {
      setError(e?.message || 'Gabim i brendshëm.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 720, mx: 'auto' }}>
      {/* ── Header card ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            {/* ── Avatar with upload overlay ── */}
            <Tooltip title="Ngarko foto profili" placement="bottom">
              <Box
                sx={{ position: 'relative', width: 72, height: 72, cursor: 'pointer', flexShrink: 0 }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Avatar
                  src={photoPreview || undefined}
                  sx={{ width: 72, height: 72, fontSize: 26, fontWeight: 700, bgcolor: roleInfo.color }}
                >
                  {!photoPreview && initials(form.firstname, form.lastname)}
                </Avatar>
                <Box sx={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity .2s',
                  '&:hover': { opacity: 1 },
                }}>
                  <CameraAltOutlinedIcon sx={{ color: '#fff', fontSize: 26 }} />
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoPick}
                />
              </Box>
            </Tooltip>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1.2 }}>
                {[form.firstname, form.lastname].filter(Boolean).join(' ') || 'Përdorues'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {form.email}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={roleInfo.label}
                  sx={{ bgcolor: roleInfo.color + '18', color: roleInfo.color, fontWeight: 600, fontSize: 12, border: `1px solid ${roleInfo.color}40` }}
                />
              </Stack>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LockResetIcon />}
              onClick={() => setCpOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
            >
              Ndrysho fjalëkalimin
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Edit form ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5, color: 'text.primary' }}>
            Informacioni personal
          </Typography>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label="Emri"
                value={form.firstname}
                onChange={handleChange('firstname')}
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon fontSize="small" color="action" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label="Mbiemri"
                value={form.lastname}
                onChange={handleChange('lastname')}
                InputProps={{ startAdornment: <InputAdornment position="start"><BadgeOutlinedIcon fontSize="small" color="action" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label="Numri i telefonit"
                value={form.phone}
                onChange={handleChange('phone')}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlinedIcon fontSize="small" color="action" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label="Pozita / Titulli i punës"
                value={form.job_title}
                onChange={handleChange('job_title')}
                InputProps={{ startAdornment: <InputAdornment position="start"><WorkOutlineIcon fontSize="small" color="action" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small"
                label="Email"
                value={form.email}
                disabled
                helperText="Emaili mund të ndryshohet vetëm nga administratori."
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon fontSize="small" color="action" /></InputAdornment> }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, px: 3 }}
            >
              {saving ? 'Duke ruajtur...' : 'Ruaj ndryshimet'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <ChangePassword open={cpOpen} onClose={() => setCpOpen(false)} userId={userId} />
    </Box>
  );
}
